import * as THREE from 'three'
import type { ComputedRef, Ref } from 'vue'
import type { SceneManager } from '@/three/SceneManager'
import { getAllThreeObjects } from '@/three/ObjectManager'
import { useSceneStore } from '@/stores/scene'

type SceneStore = ReturnType<typeof useSceneStore>

export interface ReferencePlanFloorSlabsDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  readonlyMode: ComputedRef<boolean>
  currentFloorId: Ref<string>
  exclusiveEditFloorId: Ref<string | null>
}

/**
 * 参考底图平面 + 楼层虚化楼板 + 独编楼层可见性（原 SceneCanvas 内联逻辑）
 */
export function createReferencePlanAndFloorSlabs(deps: ReferencePlanFloorSlabsDeps) {
  let referencePlanMesh: THREE.Mesh | null = null
  let floorSlabRoot: THREE.Group | null = null
  let floorSlabGeometry: THREE.PlaneGeometry | null = null
  let referencePlanLoadSerial = 0

  function disposeReferencePlanMesh() {
    if (!referencePlanMesh) return
    const sm = deps.getSm()
    if (sm) sm.scene.remove(referencePlanMesh)
    const mat = referencePlanMesh.material as THREE.MeshBasicMaterial
    if (mat.map) mat.map.dispose()
    mat.dispose()
    referencePlanMesh.geometry.dispose()
    referencePlanMesh = null
  }

  function syncReferencePlanMesh() {
    const sm = deps.getSm()
    if (!sm) return
    if (deps.readonlyMode.value) {
      disposeReferencePlanMesh()
      return
    }
    const plan = deps.sceneStore.activeScene()?.scene?.referencePlan
    if (!plan?.imageDataUrl || plan.widthM <= 0 || plan.heightM <= 0) {
      disposeReferencePlanMesh()
      return
    }
    if (plan.floorId && plan.floorId !== deps.currentFloorId.value) {
      disposeReferencePlanMesh()
      return
    }
    const fid = plan.floorId ?? deps.currentFloorId.value
    const floor = deps.sceneStore.activeFloors().find((f) => f.id === fid)
    const y = (floor?.elevation ?? 0) + 0.006
    const ox = plan.offsetX ?? 0
    const oz = plan.offsetZ ?? 0
    const opacity = Math.min(1, Math.max(0.2, plan.opacity))

    const sig = `${plan.imageDataUrl}|${plan.widthM}|${plan.heightM}`
    if (referencePlanMesh && (referencePlanMesh.userData.refPlanSig as string) === sig) {
      referencePlanMesh.position.set(ox, y, oz)
      ;(referencePlanMesh.material as THREE.MeshBasicMaterial).opacity = opacity
      sm.markDirty()
      return
    }

    disposeReferencePlanMesh()
    referencePlanLoadSerial++
    const serial = referencePlanLoadSerial

    const loader = new THREE.TextureLoader()
    loader.load(
      plan.imageDataUrl,
      (texture) => {
        if (serial !== referencePlanLoadSerial) {
          texture.dispose()
          return
        }
        texture.colorSpace = THREE.SRGBColorSpace
        const geo = new THREE.PlaneGeometry(plan.widthM, plan.heightM)
        const mat = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          opacity,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -2,
          polygonOffsetUnits: -4,
        })
        const mesh = new THREE.Mesh(geo, mat)
        mesh.rotation.x = -Math.PI / 2
        mesh.position.set(ox, y, oz)
        mesh.userData.interactable = false
        mesh.userData.refPlanSig = sig
        mesh.name = '__reference_plan__'
        mesh.renderOrder = -2
        sm.scene.add(mesh)
        referencePlanMesh = mesh
        sm.markDirty()
      },
      undefined,
      () => {}
    )
  }

  function disposeFloorSlabs() {
    if (!floorSlabRoot) return
    floorSlabRoot.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        ;(node.material as THREE.Material).dispose()
      }
    })
    const sm = deps.getSm()
    if (sm) sm.scene.remove(floorSlabRoot)
    floorSlabRoot.clear()
    floorSlabRoot = null
    floorSlabGeometry?.dispose()
    floorSlabGeometry = null
  }

  function syncFloorSlabs() {
    const sm = deps.getSm()
    if (!sm || deps.readonlyMode.value) {
      disposeFloorSlabs()
      return
    }
    const floors = deps.sceneStore.activeFloors()
    if (!floors.length) {
      disposeFloorSlabs()
      return
    }
    const groundSize = deps.sceneStore.activeScene()?.scene?.ground?.size ?? 300
    if (!floorSlabRoot || !floorSlabGeometry) {
      floorSlabRoot = new THREE.Group()
      floorSlabRoot.name = '__floor_slabs__'
      floorSlabGeometry = new THREE.PlaneGeometry(groundSize, groundSize)
      sm.scene.add(floorSlabRoot)
    } else if (Math.abs((floorSlabGeometry.parameters.width ?? 0) - groundSize) > 0.01) {
      floorSlabGeometry.dispose()
      floorSlabGeometry = new THREE.PlaneGeometry(groundSize, groundSize)
      floorSlabRoot.clear()
    }

    floorSlabRoot.clear()
    const targetFloor = deps.exclusiveEditFloorId.value
      ? floors.find((f) => f.id === deps.exclusiveEditFloorId.value)
      : null
    const targetFloorId = targetFloor?.id ?? null
    for (const floor of floors) {
      if (targetFloorId && floor.id !== targetFloorId) continue
      const isCurrent = floor.id === deps.currentFloorId.value
      const isEditTarget = deps.exclusiveEditFloorId.value && floor.id === deps.exclusiveEditFloorId.value
      const mesh = new THREE.Mesh(
        floorSlabGeometry,
        new THREE.MeshBasicMaterial({
          color: isEditTarget ? 0xf3b93f : 0x6f87a0,
          transparent: true,
          opacity: isCurrent ? 0.22 : 0.1,
          depthWrite: false,
        })
      )
      mesh.rotation.x = -Math.PI / 2
      mesh.position.set(0, floor.elevation - 0.02, 0)
      mesh.renderOrder = -1
      mesh.userData.interactable = false
      floorSlabRoot.add(mesh)
    }
    sm.markDirty()
  }

  function syncExclusiveFloorVisibility() {
    const sm = deps.getSm()
    if (!sm) return
    const objects = deps.sceneStore.activeObjects()
    const floors = deps.sceneStore.activeFloors()
    const floorMap = new Map(floors.map((f) => [f.id, f]))
    const targetFloor = deps.exclusiveEditFloorId.value ? floorMap.get(deps.exclusiveEditFloorId.value) : null
    const targetFloorId = targetFloor?.id ?? null
    const objMap = new Map(objects.map((o) => [o.id, o]))
    const threeMap = getAllThreeObjects()

    for (const [id, group] of threeMap) {
      const obj = objMap.get(id)
      if (!obj) continue
      const floor = floorMap.get(obj.floorId ?? 'f1')
      const floorId = floor?.id ?? 'f1'
      const hideByExclusive = !!targetFloorId && floorId !== targetFloorId
      group.visible = !obj.hidden && !hideByExclusive
    }
    sm.markDirty()
  }

  return {
    disposeReferencePlanMesh,
    syncReferencePlanMesh,
    disposeFloorSlabs,
    syncFloorSlabs,
    syncExclusiveFloorVisibility,
  }
}
